import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, MessageCircle, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Church Uniform",
  "Choir",
  "Conference",
  "Camp",
  "Reunion",
  "Ministry",
  "Community Group",
  "Other",
];

const initialState = {
  name: "",
  organization: "",
  email: "",
  phone: "",
  num_shirts: "",
  order_type: "Church Uniform",
  deadline: "",
  message: "",
};

export default function ContactForm() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Please add an email or phone so we can reply");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, email: form.email.trim() || undefined };
      await axios.post(`${API}/leads`, payload);
      toast.success("Got it! We'll send your free mockup within 24 hours.");
      setForm(initialState);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail[0]?.msg || "Validation error"
        : detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">
            Free design mockup
          </p>
          <h2 className="mt-3 font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Request Your <br />
            <span className="text-teal-400">Free Design Mockup</span>
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed max-w-md">
            Tell us about your group. We'll send a custom mockup and quote
            within 24 hours. No commitment until you love what you see.
          </p>

          <div className="mt-10 space-y-5">
            <ContactItem
              icon={Phone}
              label="Call us"
              value="+64 22 101 2443"
              href="tel:+64221012443"
              testid="contact-phone"
            />
            <ContactItem
              icon={MessageCircle}
              label="WhatsApp"
              value="Chat on WhatsApp"
              href="https://wa.me/64221012443"
              testid="contact-whatsapp"
            />
            <ContactItem
              icon={MapPin}
              label="Based in"
              value="Auckland, Aotearoa NZ"
              testid="contact-location"
            />
          </div>

          <div className="mt-10 flex gap-3 flex-wrap">
            <a
              href="https://www.tiktok.com/@tribaldesignzco"
              target="_blank"
              rel="noreferrer"
              data-testid="social-tiktok"
              className="px-4 py-2 border border-zinc-800 hover:border-gold hover:text-gold text-zinc-300 text-sm font-semibold uppercase tracking-wider"
            >
              TikTok @tribaldesignzco
            </a>
            <a
              href="https://instagram.com/tribaldesignznz"
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram"
              className="px-4 py-2 border border-zinc-800 hover:border-gold hover:text-gold text-zinc-300 text-sm font-semibold uppercase tracking-wider"
            >
              Instagram
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="lead-form"
          className="lg:col-span-7 bg-zinc-900 border border-zinc-800 p-7 md:p-10"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Name *" htmlFor="name">
              <Input
                id="name" required data-testid="field-name"
                value={form.name} onChange={set("name")}
                placeholder="Your full name"
                className="bg-[#060d24] border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
              />
            </Field>

            <Field label="Church or Organization" htmlFor="organization">
              <Input
                id="organization" data-testid="field-organization"
                value={form.organization} onChange={set("organization")}
                placeholder="e.g. EFKS Otara, Tongan Methodist..."
                className="bg-[#060d24] border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
              />
            </Field>

            <Field label="Email" htmlFor="email">
              <Input
                id="email" type="email" data-testid="field-email"
                value={form.email} onChange={set("email")}
                placeholder="you@example.com"
                className="bg-[#060d24] border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
              />
            </Field>

            <Field label="Phone" htmlFor="phone">
              <Input
                id="phone" data-testid="field-phone"
                value={form.phone} onChange={set("phone")}
                placeholder="+64 ..."
                className="bg-[#060d24] border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
              />
            </Field>

            <Field label="Quantity Required" htmlFor="num_shirts">
              <Input
                id="num_shirts" data-testid="field-num-shirts"
                value={form.num_shirts} onChange={set("num_shirts")}
                placeholder="e.g. 30 polos"
                className="bg-[#060d24] border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
              />
            </Field>

            <Field label="Event Type" htmlFor="order_type">
              <Select value={form.order_type} onValueChange={set("order_type")}>
                <SelectTrigger
                  id="order_type" data-testid="field-event-type"
                  className="bg-[#060d24] border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
                >
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent className="bg-[#060d24] border-zinc-800 text-white rounded-none">
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`opt-${t.replace(/\W/g,'-').toLowerCase()}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Deadline (optional)" htmlFor="deadline" className="md:col-span-2">
              <Input
                id="deadline" data-testid="field-deadline"
                value={form.deadline} onChange={set("deadline")}
                placeholder="e.g. Conference on 15 March, ASAP, 4 weeks..."
                className="bg-[#060d24] border-zinc-800 focus:border-teal-400 rounded-none h-12 text-white"
              />
            </Field>

            <Field label="Message" htmlFor="message" className="md:col-span-2">
              <Textarea
                id="message" data-testid="field-message"
                value={form.message} onChange={set("message")}
                placeholder="Tell us about your group, design ideas, colours, any cultural elements you want included..."
                rows={5}
                className="bg-[#060d24] border-zinc-800 focus:border-teal-400 rounded-none text-white resize-none"
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-lead"
            className="btn-primary mt-8 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Get My Free Mockup
                <Send size={18} />
              </>
            )}
          </button>

          <p className="mt-5 text-xs text-zinc-500">
            By submitting, you agree to be contacted about your enquiry. We never share your info.
          </p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, className = "", children }) {
  return (
    <div className={className}>
      <Label
        htmlFor={htmlFor}
        className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-semibold"
      >
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const Tag = href ? "a" : "div";
  return (
    <Tag
      href={href}
      target={href && href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      data-testid={testid}
      className="flex items-center gap-4 group"
    >
      <span className="w-11 h-11 bg-zinc-900 border border-zinc-800 grid place-items-center group-hover:border-teal-400 group-hover:text-teal-400 transition-colors">
        <Icon size={18} />
      </span>
      <span className="flex flex-col">
        <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
        <span className="font-display uppercase text-white text-base group-hover:text-teal-400 transition-colors">
          {value}
        </span>
      </span>
    </Tag>
  );
}
